import { addDays } from 'date-fns/addDays';

import { formatDateNonlocalised } from '../../../../bootstrap/localisation';
import {
  getBaseUrl,
  getDestinationId,
  getLocalisationVars,
  getOriginId,
  getSelectedMonth,
  getSelectedOutboundMonth,
  isReturn,
} from '../../selectors';
import * as serviceConstants from '../constants/dataServiceConstants';
import * as constants from '../constants/dataTypeConstants';

import type { State } from '../../selectors/types';

const INDICATIVE_PRICES_LIMIT = 180;

const formatDateForRequest = (date: Date) => {
  const year = date.getFullYear();
  const rawMonth = `0${date.getMonth() + 1}`;
  const month = rawMonth.slice(-2);
  return `${year}-${month}`;
};

type Overrides = {
  outboundDate?: Date;
  inboundDate?: Date;
  isReturn?: boolean;
};

const getDateString = (
  state: State,
  forWholeYear = false,
  overrides: Overrides = {},
) => {
  if (forWholeYear) {
    if (isReturn(state)) {
      return `${serviceConstants.CHEAPEST_DATE}/${serviceConstants.CHEAPEST_DATE}`;
    }
    return `${serviceConstants.CHEAPEST_DATE}`;
  }

  const outboundDate = overrides.outboundDate
    ? overrides.outboundDate
    : getSelectedOutboundMonth(state);
  let dateStr = formatDateForRequest(outboundDate);
  if (
    typeof overrides.isReturn === 'boolean'
      ? overrides.isReturn
      : isReturn(state)
  ) {
    const inboundDate = overrides.inboundDate
      ? overrides.inboundDate
      : getSelectedMonth(state, constants.INBOUND_DIRECTION);
    dateStr = `${dateStr}/${
      inboundDate ? formatDateForRequest(inboundDate) : dateStr
    }`;
  }
  return dateStr;
};

const getLocalisationString = ({
  currency,
  locale,
  market,
  state,
}: {
  currency?: string;
  locale?: string;
  market?: string;
  state: State;
}) => {
  const localisationVars = getLocalisationVars(state);
  if (!(
    (currency || localisationVars.currency) &&
    (market || localisationVars.market) &&
    (locale || localisationVars.locale)
  )) {
    throw new Error('Invalid market, currency or locale');
  }
  return `${market || localisationVars.market}/${
    currency || localisationVars.currency
  }/${locale || localisationVars.locale}`;
};

const getRouteString = (state: State) => {
  const originId = getOriginId(state);
  const destinationId = getDestinationId(state);
  if (!(originId && destinationId)) {
    throw new Error('Invalid origin or destination ID');
  }
  return `${originId}/${destinationId}`;
};

const getQueryString = (state: State, isWholeyear: boolean) => {
  const params: string[] = [];
  if (!isWholeyear) {
    params.push('profile=minimalmonthviewgridv2');
  }

  if (!params.length) {
    return '';
  }
  return `?${params.join('&')}`;
};

const buildQueryString = (
  state: State,
  isWholeyear: boolean,
  overrides?: Overrides,
) => {
  const baseUrl = getBaseUrl(state) || '';
  const i18n = getLocalisationString({ state });
  const route = getRouteString(state);
  const dates = getDateString(state, isWholeyear, overrides);
  const query = getQueryString(state, isWholeyear);
  return `${baseUrl}/${i18n}/calendar/${route}/${dates}/${query}`;
};

const buildMonthsQueryString = (state: State, overrides: Overrides = {}) =>
  buildQueryString(state, false, overrides);

const buildWholeYearQueryString = (state: State) =>
  buildQueryString(state, true);

const buildHotelFlexDatesQueryString = (state: State) => {
  const baseUrl = getBaseUrl(state) || '';
  const i18n = getLocalisationString({ state, market: 'UK' });
  const checkinDate = new Date();
  const checkoutDate = addDays(new Date(), INDICATIVE_PRICES_LIMIT);
  const dates = `${formatDateNonlocalised(
    checkinDate,
  )}/${formatDateNonlocalised(checkoutDate)}`;
  const originId = getOriginId(state);
  const destinationId = getDestinationId(state);

  const params = ['rooms=1', 'adults=2', 'queryType=cheapest_4_star_price'];

  const query = `?${params.join('&')}`;
  return `${baseUrl}/${i18n}/hotels/indicativeprices/${originId}/${destinationId}/${dates}/${query}`;
};

export default {
  buildMonthsQueryString,
  buildWholeYearQueryString,
  buildHotelFlexDatesQueryString,
  getLocalisationString,
};
